import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { reportError as reportErrorToService } from '../utils/errorReporting';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  isolate?: boolean; // For component-level error boundaries
  resetKeys?: Array<string | number>; // Keys that trigger reset when changed
  resetOnPropsChange?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  prevResetKeys?: Array<string | number>;
}

export class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false,
      prevResetKeys: props.resetKeys
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    const { resetKeys, resetOnPropsChange } = props;
    const { prevResetKeys, hasError } = state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys && prevResetKeys) {
      if (resetKeys.some((key, idx) => key !== prevResetKeys[idx])) {
        return {
          hasError: false,
          error: undefined,
          errorInfo: undefined,
          prevResetKeys: resetKeys
        };
      }
    }

    // Reset on any prop change if resetOnPropsChange is true
    if (hasError && resetOnPropsChange) {
      return {
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        prevResetKeys: resetKeys
      };
    }

    return { prevResetKeys: resetKeys };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Enhanced logging for debugging intermittent errors
    console.group('🔴 ErrorBoundary caught an error');
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Object:', error);
    console.error('Error Info:', errorInfo);
    console.groupEnd();

    // Report error to external service in production
    this.reportError(error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    this.setState({ error, errorInfo });
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    // Sends to Sentry when REACT_APP_SENTRY_DSN is set; no-op otherwise.
    reportErrorToService(error, {
      componentStack: errorInfo.componentStack,
      isolate: this.props.isolate,
    });

    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      isolate: this.props.isolate,
    };

    if (process.env.NODE_ENV === 'production') {
      console.error('Production error reported:', errorData);
    } else {
      console.log('Error data for debugging:', errorData);
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleRetryWithDelay = () => {
    // Retry after a short delay to prevent immediate re-error
    this.resetTimeoutId = window.setTimeout(() => {
      this.handleReset();
    }, 1000);
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Component-level error boundary (smaller, inline error)
      if (this.props.isolate) {
        return (
          <Card className="p-4 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <div className="text-center">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                Component Error
              </h3>
              <p className="text-xs text-red-600 dark:text-red-300 mb-3">
                This section encountered an error and couldn't load properly.
              </p>
              <div className="flex gap-2 justify-center">
                <Button onClick={this.handleReset} variant="outline" size="sm" className="text-xs">
                  Retry
                </Button>
              </div>
            </div>
          </Card>
        );
      }

      // Global error boundary (full screen error)
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
          <Card className="max-w-md w-full p-6 text-center">
            <div className="mb-4">
              <div className="w-12 h-12 mx-auto mb-4 text-red-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Something went wrong
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
              </p>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-4 text-left">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Error Details
                </summary>
                <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto max-h-32">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            <div className="flex gap-2 justify-center">
              <Button onClick={this.handleReset} variant="outline" size="sm">
                Try Again
              </Button>
              <Button onClick={this.handleRetryWithDelay} variant="outline" size="sm">
                Retry in 1s
              </Button>
              <Button onClick={this.handleReload} size="sm">
                Reload Page
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}